/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render capability-driven Desktop/Mobile Classroom detail editing and archive-library compositions.
 * @state-owner TanStack Query mutation/query state plus overlay-local form and stable logical-command keys
 * @allowed-imports React; React Hook Form; app environment/role provider; same-module hooks/model/schema; shared overlay, dataset, error, loading, and workspace state
 * @forbidden routes; direct API client; client-inferred authorization
 * @public-api classrooms module
 * @side-effects opens lifecycle overlays, updates Classroom details, archives/restores Classrooms, and projects accepted results
 * @tests ClassroomLifecycleAction.test.tsx and classroom-lifecycle Playwright flows
 * @security-boundary server-projected capabilities control affordances; backend authorization remains authoritative
 * @data-classification tenant-scoped Classroom names, subjects, revisions, and opaque idempotency keys
 * @trust-model active Organization, Classroom selection, form values, revisions, and capabilities are untrusted projections revalidated by FastAPI
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import {
  useArchiveClassroom,
  useRestoreClassroom,
  useUpdateClassroom,
} from '../api/classrooms.mutations';
import { useArchivedClassrooms, useClassrooms } from '../api/classrooms.queries';
import type { ClassroomSummary } from '../model/types';
import { type UpdateClassroomValues, updateClassroomSchema } from '../schemas/class-actions';
import './classroom-lifecycle.css';

export type ClassroomLifecycleSurface =
  | 'desktop'
  | 'desktop-list'
  | 'mobile-detail'
  | 'mobile-list';

function newCommandKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `agent-b-cl-f02-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function lifecycleErrorMessage(error: unknown): string {
  if (hasHttpStatus(error, 403)) return '你沒有管理這個班級的權限。';
  if (hasHttpStatus(error, 404)) return '這個班級已不存在，請重新整理。';
  if (hasHttpStatus(error, 409)) return '班級資料已變更，請關閉後重新開啟再試。';
  if (hasHttpStatus(error, 422)) return '送出的資料無法使用，請檢查後再試。';
  return error instanceof Error ? error.message : '班級更新失敗，請稍後再試。';
}

function toProjectedClassroom(classroom: ClassroomSummary) {
  return {
    code: classroom.joinCode,
    id: classroom.id,
    name: classroom.name,
    students: classroom.students,
    subtitle: classroom.subject,
  };
}

function useMobileBack(key: string, mobile: boolean) {
  const { transientNavigation } = useWorkspaceEnvironment();
  const { closeModal } = useOverlay();
  useEffect(() => {
    if (!mobile) return;
    return transientNavigation?.open(key, closeModal);
  }, [closeModal, key, mobile, transientNavigation]);
}

function ClassroomManageForm({
  classroom,
  mobile,
}: {
  classroom: ClassroomSummary;
  mobile: boolean;
}) {
  const t = useTranslator();
  const activeContextId = useActiveContextId();
  const update = useUpdateClassroom();
  const archive = useArchiveClassroom();
  const { closeModal } = useOverlay();
  const hydrate = useDatasetStore((state) => state.hydrate);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const closeMobileMain = useWorkspaceUiStore((state) => state.closeMobileMain);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const form = useForm<UpdateClassroomValues>({
    defaultValues: {
      name: classroom.name,
      subject: classroom.subject === '未設定科目' ? '' : classroom.subject,
    },
    resolver: zodResolver(updateClassroomSchema),
  });
  const updateCommand = useRef({ fingerprint: '', key: newCommandKey() });
  const archiveCommand = useRef(newCommandKey());
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
  const archiveTriggerRef = useRef<HTMLButtonElement>(null);
  const archiveConfirmRef = useRef<HTMLButtonElement>(null);
  const returnArchiveFocus = useRef(false);
  useMobileBack('classroom-lifecycle-manage', mobile);

  useEffect(() => {
    if (archiveConfirmationOpen) {
      archiveConfirmRef.current?.focus();
      return;
    }
    if (!returnArchiveFocus.current) return;
    returnArchiveFocus.current = false;
    archiveTriggerRef.current?.focus();
  }, [archiveConfirmationOpen]);

  useEffect(() => {
    const permissionLost =
      (update.isError && hasHttpStatus(update.error, 403)) ||
      (archive.isError && hasHttpStatus(archive.error, 403));
    if (!permissionLost) return;
    closeModal();
    notify(t('班級管理權限已變更，已重新載入清單'));
  }, [archive.error, archive.isError, closeModal, notify, t, update.error, update.isError]);

  const submit = form.handleSubmit(async (values) => {
    const fingerprint = JSON.stringify([values.name, values.subject]);
    if (updateCommand.current.fingerprint && updateCommand.current.fingerprint !== fingerprint) {
      updateCommand.current.key = newCommandKey();
    }
    updateCommand.current.fingerprint = fingerprint;
    try {
      const updated = await update.mutateAsync({
        activeContextId,
        classroom,
        idempotencyKey: updateCommand.current.key,
        name: values.name,
        subject: values.subject || null,
      });
      hydrate({
        classes: useDatasetStore
          .getState()
          .classes.map((item) => (item.id === updated.id ? toProjectedClassroom(updated) : item)),
      });
      closeModal();
      notify(t('班級資料已更新'));
    } catch {
      // Mutation state owns the alert below.
    }
  });

  const archiveClassroom = async () => {
    try {
      await archive.mutateAsync({
        activeContextId,
        classroom,
        idempotencyKey: archiveCommand.current,
      });
      hydrate({
        classes: useDatasetStore.getState().classes.filter((item) => item.id !== classroom.id),
      });
      pick(null);
      closeMobileMain();
      closeModal();
      notify(t('班級已封存，學生與活動記錄都已保留'));
    } catch {
      // Mutation state owns the alert below.
    }
  };

  const cancelArchive = () => {
    returnArchiveFocus.current = true;
    setArchiveConfirmationOpen(false);
  };

  const fields = archiveConfirmationOpen ? (
    <section
      aria-labelledby="classroom-archive-confirmation-title"
      className="classroom-lifecycle-confirmation"
    >
      <span aria-hidden="true" className="classroom-lifecycle-confirmation__mark">
        !
      </span>
      <div>
        <p>{t('封存前確認')}</p>
        <h3 id="classroom-archive-confirmation-title">{t(`確認封存「${classroom.name}」？`)}</h3>
        <span>{t('班級會離開目前清單；學生、活動與學習記錄都會完整保留。')}</span>
      </div>
      {archive.isError ? (
        <p className="classroom-lifecycle-error" role="alert">
          {t(lifecycleErrorMessage(archive.error))}
        </p>
      ) : null}
      <div className="classroom-lifecycle-confirmation__actions">
        <button className="bt" disabled={archive.isPending} type="button" onClick={cancelArchive}>
          {t('取消封存')}
        </button>
        <button
          className="bt classroom-lifecycle-confirmation__confirm"
          disabled={archive.isPending}
          ref={archiveConfirmRef}
          type="button"
          onClick={() => void archiveClassroom()}
        >
          {archive.isPending ? t('正在封存…') : t('確認封存')}
        </button>
      </div>
    </section>
  ) : (
    <div className="classroom-lifecycle-fields">
      <label className="classroom-create-field">
        <span>{t('班級名稱')}</span>
        <input
          aria-describedby={
            form.formState.errors.name ? 'classroom-lifecycle-name-error' : undefined
          }
          aria-invalid={Boolean(form.formState.errors.name)}
          maxLength={160}
          {...form.register('name')}
        />
        {form.formState.errors.name ? (
          <em id="classroom-lifecycle-name-error">{t(form.formState.errors.name.message ?? '')}</em>
        ) : null}
      </label>
      <label className="classroom-create-field">
        <span>{`${t('科目')} · ${t('選填')}`}</span>
        <input maxLength={160} {...form.register('subject')} />
        {form.formState.errors.subject ? (
          <em>{t(form.formState.errors.subject.message ?? '')}</em>
        ) : null}
      </label>
      {update.isError ? (
        <p className="classroom-lifecycle-error" role="alert">
          {t(lifecycleErrorMessage(update.error))}
        </p>
      ) : null}
      {classroom.canManageLifecycle ? (
        <section className="classroom-lifecycle-danger" aria-labelledby="classroom-archive-title">
          <span aria-hidden="true" className="classroom-lifecycle-danger__rail" />
          <div>
            <h3 id="classroom-archive-title">{t('收進封存庫')}</h3>
            <p>{t('班級會離開目前清單，學生、活動與學習記錄不會被刪除。')}</p>
          </div>
          <button
            className="bt classroom-lifecycle-archive"
            disabled={archive.isPending}
            ref={archiveTriggerRef}
            type="button"
            onClick={() => setArchiveConfirmationOpen(true)}
          >
            {t('封存班級')}
          </button>
        </section>
      ) : (
        <p className="classroom-lifecycle-permission-note">
          {t('你可以修改基本資料；封存與還原由班級擁有者管理。')}
        </p>
      )}
    </div>
  );

  const actions = archiveConfirmationOpen ? null : (
    <>
      <button className="bt" disabled={update.isPending} type="button" onClick={closeModal}>
        {t('取消')}
      </button>
      <button className="bt pri" disabled={update.isPending} type="submit">
        {update.isPending ? t('正在儲存…') : t('儲存變更')}
      </button>
    </>
  );

  if (mobile) {
    return (
      <section
        aria-label={t('管理班級')}
        aria-modal="true"
        className="classroom-lifecycle-mobile-sheet"
        role="dialog"
      >
        <form onSubmit={submit}>
          <header>
            <button aria-label={t('取消')} type="button" onClick={closeModal}>
              ×
            </button>
            <div>
              <p>{t('班級管理')}</p>
              <h2>{classroom.name}</h2>
            </div>
          </header>
          <div className="classroom-lifecycle-mobile-sheet__body">{fields}</div>
          {actions ? <footer>{actions}</footer> : null}
        </form>
      </section>
    );
  }

  return (
    <form onSubmit={submit}>
      <ModalDocument
        className="classroom-lifecycle-document"
        closeLabel={t('取消')}
        dialogLabel={t('管理班級')}
        footer={actions}
        maxWidth={600}
        subtitle={t('名稱與科目會同步到老師和學生看到的班級資料。')}
        title={t('管理班級')}
      >
        {fields}
      </ModalDocument>
    </form>
  );
}

function ClassroomArchiveLibrary({ mobile }: { mobile: boolean }) {
  const t = useTranslator();
  const activeContextId = useActiveContextId();
  const archived = useArchivedClassrooms(activeContextId);
  const restore = useRestoreClassroom();
  const restoreKeys = useRef(new Map<string, string>());
  const { closeModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  useMobileBack('classroom-lifecycle-archive-library', mobile);

  const restoreClassroom = async (classroom: ClassroomSummary) => {
    const fingerprint = `${classroom.id}:${classroom.revision}`;
    const idempotencyKey = restoreKeys.current.get(fingerprint) ?? newCommandKey();
    restoreKeys.current.set(fingerprint, idempotencyKey);
    try {
      await restore.mutateAsync({ activeContextId, classroom, idempotencyKey });
      notify(t(`${classroom.name} 已回到班級清單`));
    } catch {
      // Mutation state owns the alert below and the key remains stable for retry.
    }
  };

  const content = (
    <div className="classroom-archive-library">
      {restore.isError && hasHttpStatus(restore.error, 403) ? (
        <div className="classroom-archive-state" role="alert">
          <b>{t('無法繼續管理封存班級')}</b>
          <span>{t('你的班級管理權限已變更，清單正在重新載入。')}</span>
        </div>
      ) : archived.isPending ? (
        <VispoLoading label={t('正在載入封存班級')} />
      ) : archived.isError && hasHttpStatus(archived.error, 403) ? (
        <div className="classroom-archive-state" role="alert">
          <b>{t('無法開啟封存庫')}</b>
          <span>{t('你沒有查看封存班級的權限。')}</span>
        </div>
      ) : archived.isError ? (
        <div className="classroom-archive-state" role="alert">
          <b>{t('封存庫暫時無法載入')}</b>
          <span>{t(lifecycleErrorMessage(archived.error))}</span>
          <button className="bt sm" type="button" onClick={() => void archived.refetch()}>
            {t('重試')}
          </button>
        </div>
      ) : archived.data?.length ? (
        <ul className="classroom-archive-list" aria-label={t('已封存班級')}>
          {archived.data.map((classroom) => (
            <li key={classroom.id}>
              <span aria-hidden="true" className="classroom-archive-list__tab" />
              <div>
                <b>{classroom.name}</b>
                <span>{`${classroom.subject} · ${classroom.studentCount} ${t('人')}`}</span>
              </div>
              {classroom.canManageLifecycle ? (
                <button
                  className="bt"
                  disabled={restore.isPending && restore.variables?.classroom.id === classroom.id}
                  type="button"
                  onClick={() => void restoreClassroom(classroom)}
                >
                  {restore.isPending && restore.variables?.classroom.id === classroom.id
                    ? t('正在還原…')
                    : t('還原')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="classroom-archive-state" role="status">
          <span aria-hidden="true" className="classroom-archive-state__mark">
            ✓
          </span>
          <b>{t('封存庫是空的')}</b>
          <span>{t('封存的班級會保留在這裡，需要時可以還原。')}</span>
        </div>
      )}
      {restore.isError ? (
        <p className="classroom-lifecycle-error" role="alert">
          {t(lifecycleErrorMessage(restore.error))}
        </p>
      ) : null}
    </div>
  );

  if (mobile) {
    return (
      <section
        aria-label={t('班級封存庫')}
        aria-modal="true"
        className="classroom-lifecycle-mobile-sheet classroom-archive-mobile-sheet"
        role="dialog"
      >
        <div className="classroom-lifecycle-mobile-sheet__layout">
          <header>
            <button aria-label={t('關閉')} type="button" onClick={closeModal}>
              ×
            </button>
            <div>
              <p>{t('班級')}</p>
              <h2>{t('封存庫')}</h2>
            </div>
          </header>
          <div className="classroom-lifecycle-mobile-sheet__body">{content}</div>
        </div>
      </section>
    );
  }

  return (
    <ModalDocument
      className="classroom-lifecycle-document classroom-archive-document"
      closeLabel={t('關閉')}
      dialogLabel={t('班級封存庫')}
      maxWidth={680}
      subtitle={t('封存不會刪除學生、活動或學習記錄。')}
      title={t('班級封存庫')}
    >
      {content}
    </ModalDocument>
  );
}

function ArchiveIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 7.5h16M6.5 7.5v11h11v-11M9 11h6M5 4h14v3.5H5z" />
    </svg>
  );
}

export function ClassroomLifecycleAction({ surface }: { surface: ClassroomLifecycleSurface }) {
  const t = useTranslator();
  const activeContextId = useActiveContextId();
  const classrooms = useClassrooms(activeContextId);
  const selection = useWorkspaceUiStore((state) => state.selection);
  const { isModalOpen, openModal } = useOverlay();
  const trigger = useRef<HTMLButtonElement>(null);
  const opened = useRef(false);
  const mobile = surface.startsWith('mobile');
  const list = surface.endsWith('list');
  const selectionIndex = Number(/^c(\d+)$/.exec(selection ?? '')?.[1] ?? -1);
  const classroom = selectionIndex >= 0 ? classrooms.data?.[selectionIndex] : undefined;

  useEffect(() => {
    if (!opened.current || isModalOpen) return;
    opened.current = false;
    trigger.current?.focus();
  }, [isModalOpen]);

  if (classrooms.isPending || classrooms.isError) return null;

  if (list) {
    return (
      <button
        aria-label={t('班級封存庫')}
        className={
          mobile
            ? 'mobile-top-bar__icon classroom-lifecycle-library-action'
            : 'bt classroom-lifecycle-library-action classroom-lifecycle-library-action--icon'
        }
        title={t('班級封存庫')}
        type="button"
        onClick={() => {
          opened.current = true;
          openModal(<ClassroomArchiveLibrary mobile={mobile} />);
        }}
        ref={trigger}
      >
        <ArchiveIcon />
      </button>
    );
  }

  if (!classroom?.canEditBasicDetails) return null;
  return (
    <button
      className={
        mobile
          ? 'bt classroom-lifecycle-manage-action classroom-lifecycle-manage-action--mobile'
          : 'bt classroom-lifecycle-manage-action'
      }
      type="button"
      onClick={() => {
        opened.current = true;
        openModal(<ClassroomManageForm classroom={classroom} mobile={mobile} />);
      }}
      ref={trigger}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M4 18.5h4l10-10-4-4-10 10v4zM12.5 6l4 4" />
      </svg>
      {t('管理班級')}
    </button>
  );
}
